import { DatePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  InputSignal,
  ModelSignal,
  OnDestroy,
  OnInit,
  OutputEmitterRef,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LetDirective } from '@ngrx/component';
import { Observable, of } from 'rxjs';
import { filter, pairwise, tap } from 'rxjs/operators';

import { AutoFetcher } from '@core/shared/util';
import { IndustryCatalogInRelease } from '@mp/content-manager/industriekataloge/data-access';

type FetchCallback = () => Observable<IndustryCatalogInRelease[]>;

@Component({
  selector: 'mp-freigabe-queue',
  templateUrl: './freigabe-queue.component.html',
  styleUrl: './freigabe-queue.component.scss',
  imports: [LetDirective, MatProgressSpinner, MatIcon, MatCard, MatButton, DatePipe],
})
export class FreigabeQueueComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly autoFetcher = new AutoFetcher<IndustryCatalogInRelease[]>();
  readonly kataloge$ = this.autoFetcher.state$;

  readonly expanded: ModelSignal<boolean> = model<boolean>(true);
  readonly fetchInterval: InputSignal<number> = input<number>(5000);
  readonly fetchCallback: InputSignal<FetchCallback> = input<FetchCallback>(() => of([]));

  readonly dataChanged: OutputEmitterRef<void> = output<void>();

  ngOnInit(): void {
    this.autoFetcher.start(this.fetchCallback(), this.fetchInterval());
    this.kataloge$
      .pipe(
        tap(({ length }) => {
          if (length === 0) {
            this.expanded.set(false);
          }
        }),
        pairwise(),
        filter(([last, curr]) => this.dataDidChange(last, curr)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.dataChanged.emit();
        },
      });
  }

  ngOnDestroy(): void {
    this.autoFetcher.stop();
  }

  toggleExpand(): void {
    this.expanded.set(!this.expanded());
  }

  private dataDidChange(last: Array<IndustryCatalogInRelease>, current: Array<IndustryCatalogInRelease>): boolean {
    if (last.length !== current.length) return true;

    const newIds = new Set(current.map(({ catalogId }) => catalogId));

    return last.some(({ catalogId }) => !newIds.has(catalogId));
  }
}
